import {
  Box,
  Flex,
  Icon,
  IconButton,
  Link,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Tag,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Map, Set } from "immutable";
import React, { useCallback, useEffect, useState } from "react";
import {
  RiEditLine,
  RiRefreshLine,
  RiShieldStarLine,
  RiStarFill,
} from "react-icons/ri";
import { Link as RouterLink } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { FormikSwitchInput } from "src/components/Inputs/FormikSwitchInput";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { Table } from "src/components/Table/Table";
import { RelativeTime } from "src/components/Text/RelativeTime";
import useAccessToken from "src/hooks/useAccessToken";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { Env, useEnv } from "src/services/env";
import * as Url from "src/services/url";
import { AuthData, Status } from "src/types/authData";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import { z } from "zod";

export const ExploreList: React.FC<Props> = ({ data }) => {
  const organizationRD = useOrganization();
  const isAvelaAdmin = useRequiredHasuraRoles([HasuraRole.ADMIN]);
  const toast = useAvelaToast();
  const { instances, timestamps, refreshInstance } = data;
  const [refreshState, setRefreshState] = useState<Set<string>>(Set());

  return (
    <RemoteDataView
      error={(error) => <GenericError error={error} />}
      remoteData={instances}
      loading={
        <Flex direction="column" gap="4">
          <Flex gap="2">
            <Skeleton width="2rem" height="1.5rem" />
            <Skeleton width="6rem" height="1.5rem" />
          </Flex>
          <Flex direction="column" gap="2" marginLeft={9}>
            <Skeleton width="100%" height="3rem" />
            <Skeleton width="100%" height="3rem" />
            <Skeleton width="100%" height="3rem" />
          </Flex>
        </Flex>
      }
    >
      {(data) => {
        return (
          <Flex direction="column" gap={4}>
            <Flex direction="row" alignItems="center" gap={2}>
              <FormikSwitchInput<"enabled" | "disabled">
                isDisabled={true}
                label="Explore"
                name="explore.status"
                checked={["enabled", ""]}
                unchecked={["disabled", ""]}
                direction="row"
                formLabelProps={{
                  fontSize: "md",
                  alignItems: "center",
                  alignSelf: "center",
                  margin: 0,
                }}
              />
              {data.length <= 0 ? (
                <Popover trigger="hover" placement="right">
                  <PopoverTrigger>
                    <IconButton
                      icon={<RiShieldStarLine />}
                      aria-label="Upgrade to unlock explore"
                      size="md"
                      variant="ghost"
                    />
                  </PopoverTrigger>
                  <PopoverContent shadow="lg" width="25rem">
                    <Flex
                      direction="column"
                      margin={6}
                      gap="2"
                      alignItems="flex-start"
                    >
                      <Tag
                        size="md"
                        variant="solid"
                        colorScheme="purple"
                        mb={2}
                      >
                        <Icon boxSize={4} as={RiStarFill} paddingRight={1} />
                        <Text size="xs">Pro</Text>
                      </Tag>
                      <Text fontSize="lg" fontWeight="bold">
                        Upgrade to unlock Explore
                      </Text>
                      <Text>
                        Empower families to discover schools and conduct their
                        own research.{" "}
                        <Link
                          textDecoration="underline"
                          href="https://avela.org/explore"
                          target="_blank"
                        >
                          Learn more.
                        </Link>
                      </Text>
                    </Flex>
                  </PopoverContent>
                </Popover>
              ) : null}
            </Flex>
            {data.length > 0 ? (
              <Flex direction="column" gap={2} marginLeft={12}>
                <Table
                  tableHeader={() => <></>}
                  data={data}
                  columns={[
                    {
                      id: "1",
                      header: "Instance",
                      accessorFn: (row) => row.domain,
                      size: 1000,
                    },
                    {
                      id: "2",
                      header: "Actions",
                      cell: ({ row }) => {
                        const timestamp =
                          timestamps.get(row.original.path) ?? RD.loading();
                        return (
                          <Flex
                            gap={2}
                            alignItems="center"
                            justifyContent="flex-end"
                          >
                            <RemoteDataView
                              remoteData={timestamp}
                              error={() => (
                                <GenericError
                                  message="Error retrieving last update"
                                  variant="inline"
                                />
                              )}
                              loading={
                                <Skeleton height="1.5rem" width="10rem" />
                              }
                            >
                              {(data) => (
                                <Box>
                                  Last refreshed{" "}
                                  {<RelativeTime timestamp={data} />} ago
                                </Box>
                              )}
                            </RemoteDataView>
                            <Tooltip label="Refresh configurations" hasArrow>
                              <IconButton
                                isLoading={refreshState.has(row.original.path)}
                                aria-label="Refresh configurations"
                                variant="outline"
                                colorScheme="gray"
                                icon={<RiRefreshLine />}
                                onClick={async () => {
                                  try {
                                    setRefreshState((state) =>
                                      state.add(row.original.path)
                                    );
                                    await refreshInstance(row.original.path);
                                  } catch (error) {
                                    console.error(error);
                                    toast.error({
                                      title: "Instance could not be refreshed",
                                      description:
                                        error instanceof Error
                                          ? error.message
                                          : "Unknown error",
                                    });
                                  } finally {
                                    setRefreshState((state) =>
                                      state.remove(row.original.path)
                                    );
                                  }
                                }}
                              />
                            </Tooltip>

                            {isAvelaAdmin ? (
                              <Tooltip label="Edit configurations" hasArrow>
                                <IconButton
                                  as={RouterLink}
                                  to={organizationRD
                                    .map((organization) =>
                                      Url.OrgAdmin.Org.editExplore(
                                        organization,
                                        row.original.path
                                      )
                                    )
                                    .withDefault("#")}
                                  aria-label="Edit configurations"
                                  variant="outline"
                                  colorScheme="gray"
                                  icon={<RiEditLine />}
                                />
                              </Tooltip>
                            ) : null}
                          </Flex>
                        );
                      },
                    },
                  ]}
                />
              </Flex>
            ) : null}
          </Flex>
        );
      }}
    </RemoteDataView>
  );
};

/** Hooks */
export const FormikSchema = z.object({
  explore: z.object({
    status: z.union([z.literal("enabled"), z.literal("disabled")]),
  }),
});
export type FormikType = z.infer<typeof FormikSchema>;

export function useForm(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  data: ExploreData;
} {
  const explore = useExploreList();
  return React.useMemo(
    () => ({
      data: explore,
      initialValues: explore.instances.map((data): FormikType => {
        return {
          explore: {
            status: data.length > 0 ? "enabled" : "disabled",
          },
        };
      }),
    }),
    [explore]
  );
}

const useExploreList = (): ExploreData => {
  const env = useEnv();
  const organizationRD = useOrganization();

  const [remoteData, setRemoteData] = useState<
    RD.RemoteData<Error, ExploreInstance[]>
  >(RD.loading());
  const [timestamps, setTimestamps] = useState(Map<string, TimestampRD>());

  useEffect(() => {
    const goFetch = async () => {
      try {
        if (!organizationRD.hasData() || remoteData.hasData()) {
          return;
        }

        const organization = organizationRD.data;
        const exploreInstances = await fetchExploreList(env, organization.path);
        const timestampsResponses = await Promise.all(
          exploreInstances.map(({ path }) =>
            fetchTimestamp(env, path).then(
              (t) => [path, t] satisfies [string, TimestampRD]
            )
          )
        );
        setRemoteData(RD.success(exploreInstances));
        setTimestamps(Map(timestampsResponses));
      } catch (error) {
        console.error(error);
        if (error instanceof Error) {
          setRemoteData(RD.failure(error));
        } else {
          setRemoteData(
            RD.failure(new Error("Unknown error", { cause: error }))
          );
        }
      }
    };
    goFetch();
  }, [env, organizationRD, remoteData]);

  const accessToken = useAccessToken();
  const pollTimestampChanges = useCallback(
    async (
      explorePath: string,
      currentTimestamp: TimestampRD | undefined
    ): Promise<void> => {
      const pollInterval = 2_000; /* ms */
      const pollDuration = 300_000; /* ms = 5 minutes */
      const maxPolling = Math.round(pollDuration / pollInterval);
      let polling = 0;

      return new Promise((resolve, reject) => {
        const intervalId = setInterval(async () => {
          try {
            // Check if we reach the maximum polling attempts
            if (polling >= maxPolling) {
              clearInterval(intervalId);
              reject(
                new Error(
                  "Refresh operation timed out. Please contact the Avela team to help troubleshoot this error."
                )
              );
              return;
            }

            const updatedTimestamp = await fetchTimestamp(env, explorePath);

            // Check if timestamp has changed
            if (!isEqual(currentTimestamp, updatedTimestamp)) {
              setTimestamps(timestamps.set(explorePath, updatedTimestamp));
              clearInterval(intervalId);
              resolve();
              return;
            }

            polling++;
          } catch (error) {
            console.error("Error polling timestamp:", error);
            clearInterval(intervalId);
            reject(error);
          }
        }, pollInterval);
      });
    },
    [env, timestamps]
  );

  const refreshInstance = useCallback(
    async (explorePath: string) => {
      const currentTimestamp = timestamps.get(explorePath);
      await refreshExplore(env, accessToken, explorePath);
      await pollTimestampChanges(explorePath, currentTimestamp);
    },
    [accessToken, env, pollTimestampChanges, timestamps]
  );

  return { instances: remoteData, refreshInstance, timestamps };
};

/** API Calls */

async function fetchExploreList(
  env: Env,
  applyPath: string
): Promise<ExploreInstance[]> {
  const response = await fetch(
    `${env.VITE_EXPLORE_SERVICE_URL}/mapping/apply/${applyPath}`,
    {
      method: "GET",
    }
  );
  const json = await response.json();
  const exploreInstances = ExploreInstanceList.parse(json);
  return exploreInstances;
}

async function fetchTimestamp(
  env: Env,
  explorePath: string
): Promise<TimestampRD> {
  try {
    const response = await fetch(
      `${env.VITE_EXPLORE_SERVICE_URL}/metadata/${explorePath}/en.data.json`,
      { method: "GET" }
    );
    const json = await response.json();
    return RD.success(TimestampSchema.parse(json).updated_at);
  } catch (error) {
    return RD.failure(
      error instanceof Error
        ? error
        : new Error("Unknown error", { cause: error })
    );
  }
}

async function refreshExplore(
  env: Env,
  accessToken: AuthData<string>,
  explorePath: string
) {
  if (accessToken.status !== Status.OK) {
    throw new Error("Missing access token");
  }

  await fetch(`${env.VITE_EXPLORE_SERVICE_URL}/refresh/${explorePath}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken.data}`,
    },
  });
}

/** Types */
type Props = {
  data: ExploreData;
};
const ExploreInstanceSchema = z.object({
  path: z.string(),
  domain: z.string(),
});
type ExploreInstance = z.infer<typeof ExploreInstanceSchema>;
const ExploreInstanceList = z.array(ExploreInstanceSchema);
const TimestampSchema = z.object({ updated_at: z.string() });
type TimestampRD = RD.RemoteData<Error, string>;

type ExploreData = {
  instances: RD.RemoteData<Error, ExploreInstance[]>;
  timestamps: Map<string, RD.RemoteData<Error, string>>;
  refreshInstance: (explorePath: string) => Promise<void>;
};

/** Helpers function */
function isEqual(a: TimestampRD | undefined, b: TimestampRD | undefined) {
  return a?.hasData() && b?.hasData() && a.data === b.data;
}
